import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Container, Row, Col, Alert, Card, Button, Placeholder, Dropdown, Form, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  getAllAccounts,
  getTradingStatistics,
  createAccount,
  getPortfolios,
  getLastUpdateTime,
  getSelectedStatusTickers,
  getTodayGainIntradayHistory
} from '../services/api';
import AccountPortfolioTable from '../components/AccountPortfolioTable';
import ColumnVisibilityDropdown from '../components/ColumnVisibilityDropdown';
import AccountsChartView from '../components/AccountsChartView';
import PieChartModal from '../components/PieChartModal';
import { subscribeToTickerUpdates, subscribeToMarketIndexUpdates } from '../services/socketService';
import BalanceHistoryModal from '../components/BalanceHistoryModal';
import PerformanceChartModal from '../components/PerformanceChartModal';
import EdgeHistoryModal from '../components/EdgeHistoryModal';
import { formatBalanceDisplay, normalizeNearZero } from '../utils/formatBalance';
import { formatISODate, isMarketOpen } from '../utils/dateUtils';
import {
  initializeColumnConfig,
  saveAsDefaultColumnConfig,
  resetToDefaultColumnConfig,
  getDefaultColumnConfig
} from '../utils/columnPreferences';
import {
  getAccountColumnDefinitions,
  isAccountNumericColumn
} from '../utils/accountTableConfig';

const defaultColumnOrder = [
  'portfolio',
  'portfolioInfo',
  'description',
  'balance',
  'cash',
  'edge',
  'today_gain_amount',
  'today_gain_percentage',
  'total_gain_amount',
  'total_gain_percentage',
  'ytd_gain_amount',
  'ytd_gain_percentage'
];

const defaultVisibleColumns = {
  portfolio: true,
  portfolioInfo: true,
  description: true,
  balance: true,
  cash: true,
  edge: true,
  today_gain_amount: true,
  today_gain_percentage: true,
  total_gain_amount: true,
  total_gain_percentage: true,
  ytd_gain_amount: true,
  ytd_gain_percentage: true
};

const chartModeExcludedColumns = new Set(['portfolio', 'portfolioInfo']);
const CHART_COLUMN_STORAGE_KEY = 'allAccountsSelectedChartColumn';
const MARKET_STATUS_STORAGE_KEY = 'allAccounts-marketClosed';
const MAX_INTRADAY_CHART_POINTS = 60;

const readStoredMarketClosedStatus = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }
  try {
    return window.localStorage.getItem(MARKET_STATUS_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

const persistMarketClosedStatus = (isClosed) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(MARKET_STATUS_STORAGE_KEY, isClosed ? 'true' : 'false');
  } catch (error) {
    // Ignore storage issues so UI logic keeps working
  }
};

const downsampleIntradayEntries = (entries = [], maxPoints = MAX_INTRADAY_CHART_POINTS) => {
  if (!Array.isArray(entries) || entries.length <= maxPoints) {
    return entries;
  }

  const step = Math.max(1, Math.ceil(entries.length / maxPoints));
  const downsampled = [];

  for (let index = 0; index < entries.length; index += step) {
    downsampled.push(entries[index]);
  }

  const lastEntry = entries[entries.length - 1];
  if (downsampled[downsampled.length - 1] !== lastEntry) {
    downsampled.push(lastEntry);
  }

  return downsampled;
};

const normalizeIntradayEntries = (entries = []) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  const normalized = entries
    .map(entry => {
      const timestamp = entry?.timestamp;
      const rawAmount = entry?.amount;
      const amount = typeof rawAmount === 'number' ? rawAmount : Number(rawAmount);
      if (!timestamp || Number.isNaN(amount)) {
        return null;
      }
      return {
        timestamp,
        amount: Math.round(amount)
      };
    })
    .filter(Boolean);

  return downsampleIntradayEntries(normalized);
};


// Custom styles for the component
const customStyles = `
    /* Enhanced card styling for maximum contrast and prominence */
    .accounts-card {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out !important;
        min-height: 140px !important;
    }

    .accounts-card:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 8px 24px var(--shadow-color) !important;
    }
    
    [data-theme="light"] .accounts-card {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="light"] .accounts-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="dark"] .accounts-card {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    
    [data-theme="dark"] .accounts-card:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
    }
    
    /* Enhanced table container styling */
    .accounts-table-container {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        overflow: hidden !important;
        margin-bottom: 1rem !important;
    }
    
    [data-theme="light"] .accounts-table-container {
        background-color: #ffffff !important;
        border-color: #d1d5db !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="dark"] .accounts-table-container {
        background-color: #1f2937 !important;
        border-color: #4b5563 !important;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
    }
    
    /* Enhanced table styling */
    .accounts-table-container .account-portfolio-table {
        background-color: var(--card-bg) !important;
        border: none !important;
        margin-bottom: 0 !important;
    }
    
    /* Remove gap between last row and bottom of table container */
    .accounts-table-container .account-portfolio-table {
        margin-bottom: 0 !important;
    }
    
    /* Enhanced table header styling */
    .accounts-table-container .account-portfolio-table thead th,
    .accounts-table-container .accounts-table-header {
        background-color: var(--bg-secondary) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        transition: background-color 0.2s ease-in-out;
    }
    
    .accounts-table-container .account-portfolio-table thead th:hover,
    .accounts-table-container .accounts-table-header:hover {
        background-color: var(--hover-bg) !important;
        cursor: pointer;
    }
    
    [data-theme="light"] .accounts-table-container .account-portfolio-table thead th,
    [data-theme="light"] .accounts-table-container .accounts-table-header {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .accounts-table-container .account-portfolio-table thead th:hover,
    [data-theme="light"] .accounts-table-container .accounts-table-header:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .accounts-table-container .account-portfolio-table thead th,
    [data-theme="dark"] .accounts-table-container .accounts-table-header {
        background-color: #374151 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .accounts-table-container .account-portfolio-table thead th:hover,
    [data-theme="dark"] .accounts-table-container .accounts-table-header:hover {
        background-color: #4b5563 !important;
    }
    
    /* Enhanced table row styling with hover effects - more prominent and brighter */
    .accounts-table-container .account-portfolio-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        transition: all 0.3s ease-in-out !important;
        font-size: 0.95rem !important;
    }
    
    .accounts-table-container .account-portfolio-table tbody tr:hover {}
    [data-theme="light"] .accounts-table-container .account-portfolio-table tbody tr:hover {}
    [data-theme="dark"] .accounts-table-container .account-portfolio-table tbody tr:hover {}
    
    /* Enhanced table cell styling - more compact with hover effects */
    .accounts-table-container .account-portfolio-table tbody td {
        padding: 4px 6px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
        transition: all 0.3s ease-in-out !important;
    }
    
    .accounts-table-container .account-portfolio-table tbody tr:hover td {
        color: var(--text-primary) !important;
        font-weight: 500 !important;
        transform: scale(1.02) !important;
    }
    
    [data-theme="light"] .accounts-table-container .account-portfolio-table tbody td {
        border-bottom: 1px solid #e5e7eb !important;
    }
    
    [data-theme="light"] .accounts-table-container .account-portfolio-table tbody tr:hover td {
        color: #1e40af !important;
    }
    
    [data-theme="dark"] .accounts-table-container .account-portfolio-table tbody td {
        border-bottom: 1px solid #374151 !important;
    }
    
    [data-theme="dark"] .accounts-table-container .account-portfolio-table tbody tr:hover td {
        color: #93c5fd !important;
    }
    
    /* Enhanced total row styling - match header styling exactly */
    .accounts-table-container .account-portfolio-table .table-total {
        background-color: var(--bg-secondary) !important;
        border-top: 2px solid var(--border-color) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
    }
    
    .accounts-table-container .account-portfolio-table .table-total td {
        background-color: var(--bg-secondary) !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        border-bottom: none !important;
    }
    
    .accounts-table-container .account-portfolio-table .table-total:hover {
        background-color: var(--hover-bg) !important;
        cursor: default !important;
        transform: none !important;
        box-shadow: none !important;
    }
    
    [data-theme="light"] .accounts-table-container .account-portfolio-table .table-total {
        background-color: #f8f9fa !important;
        border-top: 2px solid #d1d5db !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .accounts-table-container .account-portfolio-table .table-total td {
        background-color: #f8f9fa !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .accounts-table-container .account-portfolio-table .table-total:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .accounts-table-container .account-portfolio-table .table-total {
        background-color: #374151 !important;
        border-top: 2px solid #4b5563 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .accounts-table-container .account-portfolio-table .table-total td {
        background-color: #374151 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .accounts-table-container .account-portfolio-table .table-total:hover {
        background-color: #4b5563 !important;
    }
    
    /* CSS Variables for theme support */
    :root {
        --card-bg: #ffffff;
        --border-color: #d1d5db;
        --shadow-color: rgba(0, 0, 0, 0.1);
        --text-primary: #374151;
        --bg-secondary: #f8f9fa;
        --hover-bg: #f1f5f9;
    }
    
    [data-theme="dark"] {
        --card-bg: #1f2937;
        --border-color: #4b5563;
        --shadow-color: rgba(0, 0, 0, 0.3);
        --text-primary: #f9fafb;
        --bg-secondary: #374151;
        --hover-bg: #4b5563;
    }
    
    /* Prevent scrollbar from appearing on hover */
    .table-responsive:hover {
        overflow: hidden !important;
    }
    
    /* Account card specific styling */
    .account-card {
        transition: all 0.15s ease-in-out !important;
        cursor: pointer !important;
        min-height: 140px !important;
        border-width: 1px !important;
    }
    
    .clickable-card:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 6px 20px var(--shadow-color) !important;
        border-width: 1.5px !important;
    }
    
    [data-theme="light"] .clickable-card:hover {
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
    }
    
    [data-theme="dark"] .clickable-card:hover {
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35) !important;
    }
    
    /* Cards container styling */
    .accounts-cards-container {
        margin-bottom: 1rem;
    }
    
    /* Compact spacing adjustments */
    .account-card .card-body .row {
        margin: 0 !important;
    }
    
    .account-card .card-body .row > * {
        padding-left: 1px !important;
        padding-right: 1px !important;
    }
    
    /* Remove link color changes on hover for cards */
    .account-card a:hover {
        color: inherit !important;
        text-decoration: none !important;
    }

    /* Ensure card content doesn't affect link styling */
    .account-card .card-title a {
        color: inherit !important;
    }

`;

function AllAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPieChartModal, setShowPieChartModal] = useState(false);
  const [showBalanceHistoryModal, setShowBalanceHistoryModal] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [showEdgeHistoryModal, setShowEdgeHistoryModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountDescription, setNewAccountDescription] = useState('');
  const [newAccountCash, setNewAccountCash] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [portfolios, setPortfolios] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState('');
  const [overallExpectancy, setOverallExpectancy] = useState(null);
  const marketClosedRef = useRef(readStoredMarketClosedStatus());
  const updateMarketClosedState = useCallback((nextValue) => {
    if (typeof nextValue !== 'boolean') {
      return false;
    }
    const previous = marketClosedRef.current;
    if (previous === nextValue) {
      return false;
    }
    marketClosedRef.current = nextValue;
    persistMarketClosedStatus(nextValue);
    return previous && !nextValue;
  }, []);
  const [accountEdgeMap, setAccountEdgeMap] = useState({});
  const fetchedEdgesRef = useRef(new Set());
  const fetchEdgesTimeoutRef = useRef(null);
  const accountColumnDefinitions = useMemo(() => getAccountColumnDefinitions('account'), []);
  // View mode state - default to table view
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('accountsViewMode');
    return savedViewMode || 'table';
  });
  const [selectedChartColumn, setSelectedChartColumn] = useState(() => {
    if (typeof window === 'undefined') {
      return 'today_gain_amount';
    }

    const storedColumn = localStorage.getItem(CHART_COLUMN_STORAGE_KEY);
    return storedColumn || 'today_gain_amount';
  });
  // Edge history modal state
  const [edgeHistoryTimePeriod, setEdgeHistoryTimePeriod] = useState('1month');
  const [edgeHistoryAccountId, setEdgeHistoryAccountId] = useState('all');
  const [showSaveDefaultModal, setShowSaveDefaultModal] = useState(false);
  const [showResetSystemDefaultModal, setShowResetSystemDefaultModal] = useState(false);
  
  // Add sorting state with localStorage persistence
  const [sortConfig, setSortConfig] = useState(() => {
    const savedSortConfig = localStorage.getItem('sortConfig-allAccounts');
    return savedSortConfig ? JSON.parse(savedSortConfig) : { key: 'balance', direction: 'desc' };
  });
  
  const [visibleColumns, setVisibleColumns] = useState(() => ({ ...defaultVisibleColumns }));
  const [columnOrder, setColumnOrder] = useState(() => [...defaultColumnOrder]);
  const [columnsInitialized, setColumnsInitialized] = useState(false);
  const [todayGainIntradaySeries, setTodayGainIntradaySeries] = useState([]);
  const [statusTickers, setStatusTickers] = useState([]);
  const todayGainHistoryLoadedRef = useRef(false);
  const [intradayPeriod, setIntradayPeriod] = useState(1); // 1, 2, 3, or 5 days
  const chartColumnOrder = useMemo(() => {
    const baseOrder = defaultColumnOrder.filter(columnKey => (
      isAccountNumericColumn(columnKey)
      && !chartModeExcludedColumns.has(columnKey)
      && accountColumnDefinitions[columnKey]
    ));
    const extras = Object.keys(accountColumnDefinitions).filter(columnKey => (
      isAccountNumericColumn(columnKey)
      && !chartModeExcludedColumns.has(columnKey)
      && !baseOrder.includes(columnKey)
    ));
    return [...baseOrder, ...extras];
  }, [accountColumnDefinitions]);
  const availableChartColumns = useMemo(() => chartColumnOrder, [chartColumnOrder]);


  const sanitizeVisibleColumns = useCallback((columns = {}) => ({
    ...defaultVisibleColumns,
    ...columns
  }), []);

  const sanitizeColumnOrder = useCallback((order = []) => {
    const providedOrder = Array.isArray(order) ? [...order] : [];
    let sanitized = providedOrder.filter(column => defaultColumnOrder.includes(column));

    defaultColumnOrder.forEach(column => {
      if (!sanitized.includes(column)) {
        sanitized.push(column);
      }
    });

    if (!providedOrder.includes('edge')) {
      const withoutEdge = sanitized.filter(column => column !== 'edge');
      const cashIndex = withoutEdge.indexOf('cash');
      if (cashIndex !== -1) {
        withoutEdge.splice(cashIndex + 1, 0, 'edge');
        sanitized = withoutEdge;
      }
    }

    return sanitized;
  }, []);

  const updateColumnPreferencesState = useCallback((visible, order) => {
    setVisibleColumns(sanitizeVisibleColumns(visible));
    setColumnOrder(sanitizeColumnOrder(order));
    setColumnsInitialized(true);
  }, [sanitizeVisibleColumns, sanitizeColumnOrder]);

  const clearColumnStorage = useCallback(() => {
    localStorage.removeItem('visibleColumns-allAccounts');
    localStorage.removeItem('columnOrder-allAccounts');
  }, []);

  const applyIntradayTimelinePayload = useCallback((payload) => {
    const normalizedEntries = normalizeIntradayEntries(payload?.entries || []);
    setTodayGainIntradaySeries(normalizedEntries);
    todayGainHistoryLoadedRef.current = true;
  }, []);

  const fetchTodayGainIntradayHistory = useCallback(async () => {
    try {
      const response = await getTodayGainIntradayHistory(intradayPeriod);
      applyIntradayTimelinePayload(response.data);
    } catch (error) {
      console.error('Failed to load intraday gain history', error);
      todayGainHistoryLoadedRef.current = true;
    }
  }, [applyIntradayTimelinePayload, intradayPeriod]);

  const fetchStatusTickers = useCallback(async () => {
    try {
      const response = await getSelectedStatusTickers();
      if (Array.isArray(response.data)) {
        setStatusTickers(response.data);
      } else {
        setStatusTickers([]);
      }
    } catch (error) {
      console.error('Failed to load status bar tickers', error);
      setStatusTickers([]);
    }
  }, []);

  const updateStatusTickersFromPayload = useCallback((updatedTickers) => {
    if (!Array.isArray(updatedTickers) || updatedTickers.length === 0) {
      return;
    }
    setStatusTickers((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return prev;
      }
      const updateMap = new Map();
      updatedTickers.forEach((ticker) => {
        if (ticker?.symbol) {
          updateMap.set(ticker.symbol, ticker);
        }
      });
      if (updateMap.size === 0) {
        return prev;
      }
      let changed = false;
      const next = prev.map((entry) => {
        const symbol = entry?.symbol;
        if (!symbol || !updateMap.has(symbol)) {
          return entry;
        }
        const update = updateMap.get(symbol);
        const updatedEntry = {
          ...entry,
          current_price: update?.current_price ?? entry?.current_price,
          previous_close: update?.previous_close ?? entry?.previous_close,
          percent_change: typeof update?.percent_change === 'number'
            ? update.percent_change
            : entry?.percent_change,
          last_updated: update?.last_updated ?? entry?.last_updated
        };
        if (
          updatedEntry.current_price !== entry?.current_price ||
          updatedEntry.previous_close !== entry?.previous_close ||
          updatedEntry.percent_change !== entry?.percent_change ||
          updatedEntry.last_updated !== entry?.last_updated
        ) {
          changed = true;
        }
        return updatedEntry;
      });
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadColumnPreferences = async () => {
      try {
        const config = await initializeColumnConfig('all_accounts');
        if (!isMounted) {
          return;
        }

        updateColumnPreferencesState(config.visibleColumns, config.columnOrder);
      } catch (err) {
        console.error('Failed to initialize column configuration for all accounts', err);
        if (!isMounted) {
          return;
        }

        const savedColumns = localStorage.getItem('visibleColumns-allAccounts');
        const savedOrder = localStorage.getItem('columnOrder-allAccounts');

        if (savedColumns && savedOrder) {
          try {
            updateColumnPreferencesState(JSON.parse(savedColumns), JSON.parse(savedOrder));
          } catch (parseError) {
            console.error('Failed to parse saved column preferences for all accounts', parseError);
            updateColumnPreferencesState(defaultVisibleColumns, defaultColumnOrder);
          }
        } else {
          updateColumnPreferencesState(defaultVisibleColumns, defaultColumnOrder);
        }
      }
    };

    loadColumnPreferences();

    return () => {
      isMounted = false;
    };
  }, [updateColumnPreferencesState]);

  useEffect(() => {
    fetchTodayGainIntradayHistory();
  }, [fetchTodayGainIntradayHistory]);

  // Add periodic fetch during market hours to ensure chart updates
  // even if WebSocket ticker updates are delayed
  useEffect(() => {
    // Only set up periodic fetch during market hours
    if (!isMarketOpen()) {
      return;
    }

    // Fetch immediately on mount during market hours
    fetchTodayGainIntradayHistory();

    // Set up interval to fetch every minute during market hours
    const intervalId = setInterval(() => {
      if (isMarketOpen()) {
        fetchTodayGainIntradayHistory();
      }
    }, 60 * 1000); // 60 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchTodayGainIntradayHistory]);

  useEffect(() => {
    fetchStatusTickers();
  }, [fetchStatusTickers]);

  useEffect(() => {
    const unsubscribe = subscribeToMarketIndexUpdates((payload) => {
      const indexes = Array.isArray(payload?.indexes)
        ? payload.indexes
        : Array.isArray(payload?.market_data)
          ? payload.market_data
          : null;

      if (indexes) {
        setStatusTickers(indexes);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  // Market conditions state - now using context
  // const [selectedMarketCondition, setSelectedMarketCondition] = useState(() => {
  //   const savedCondition = localStorage.getItem('marketCondition-allAccounts');
  //   return savedCondition || '2'; // Default to "Market is OK"
  // });

  // Ensure newly added 'edge' column is present in saved columnOrder
  useEffect(() => {
    if (!columnsInitialized || !Array.isArray(columnOrder)) {
      return;
    }
    if (!columnOrder.includes('edge')) {
      const newOrder = [...columnOrder];
      const cashIdx = newOrder.indexOf('cash');
      const insertAt = cashIdx >= 0 ? cashIdx + 1 : newOrder.length;
      newOrder.splice(insertAt, 0, 'edge');
      setColumnOrder(newOrder);
    }
  }, [columnOrder, columnsInitialized]);

  // Save column visibility settings when they change
  useEffect(() => {
    if (!columnsInitialized) {
      return;
    }
    localStorage.setItem('visibleColumns-allAccounts', JSON.stringify(visibleColumns));
  }, [visibleColumns, columnsInitialized]);

  // Save column order when it changes
  useEffect(() => {
    if (!columnsInitialized) {
      return;
    }
    localStorage.setItem('columnOrder-allAccounts', JSON.stringify(columnOrder));
  }, [columnOrder, columnsInitialized]);

  useEffect(() => {
    setSelectedChartColumn(current => {
      if (!availableChartColumns.length) {
        return null;
      }
      if (current && availableChartColumns.includes(current)) {
        return current;
      }
      return availableChartColumns[0];
    });
  }, [availableChartColumns]);

  const handleResetColumnsToUserDefaults = async () => {
    if (!columnsInitialized) {
      alert('❌ Column preferences are still loading.\n\nPlease wait for the page to finish initializing and try again.');
      return;
    }

    try {
      const config = await getDefaultColumnConfig('all_accounts');
      clearColumnStorage();
      updateColumnPreferencesState(config.visibleColumns, config.columnOrder);
    } catch (error) {
      console.error('Failed to reset column configuration to saved defaults for all accounts', error);
      clearColumnStorage();
      updateColumnPreferencesState(defaultVisibleColumns, defaultColumnOrder);
    }
  };

  const handleResetColumnsToSystemDefaults = async () => {
    if (!columnsInitialized) {
      alert('❌ Column preferences are still loading.\n\nPlease wait for the page to finish initializing and try again.');
      return;
    }

    setShowResetSystemDefaultModal(true);
  };

  const confirmResetColumnsToSystemDefaults = async () => {
    setShowResetSystemDefaultModal(false);
    try {
      const config = await resetToDefaultColumnConfig('all_accounts');
      clearColumnStorage();
      updateColumnPreferencesState(config.visibleColumns, config.columnOrder);
    } catch (error) {
      console.error('Failed to reset column configuration to system defaults for all accounts', error);
      alert('❌ Failed to reset to system default columns.\n\nPlease try again.\n\nError: ' + error.message);
    }
  };

  const handleChartHeaderClick = useCallback((columnKey) => {
    if (!columnKey || !isAccountNumericColumn(columnKey)) {
      return;
    }

    const sortKey = accountColumnDefinitions[columnKey]?.key || columnKey;

    setSortConfig(prev => {
      if (prev.key === sortKey && prev.direction === 'desc') {
        return prev;
      }
      return { key: sortKey, direction: 'desc' };
    });

    setSelectedChartColumn(current => (current === columnKey ? current : columnKey));
  }, [accountColumnDefinitions]);

  useEffect(() => {
    if (!availableChartColumns || availableChartColumns.length === 0) {
      if (selectedChartColumn !== '') {
        setSelectedChartColumn('');
      }
      return;
    }

    if (!selectedChartColumn || !availableChartColumns.includes(selectedChartColumn)) {
      const preferredColumn = availableChartColumns.includes('today_gain_amount')
        ? 'today_gain_amount'
        : availableChartColumns.includes('balance')
          ? 'balance'
          : availableChartColumns[0];

      if (preferredColumn && preferredColumn !== selectedChartColumn) {
        setSelectedChartColumn(preferredColumn);
      }
    }
  }, [availableChartColumns, selectedChartColumn]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (selectedChartColumn) {
      localStorage.setItem(CHART_COLUMN_STORAGE_KEY, selectedChartColumn);
    } else {
      localStorage.removeItem(CHART_COLUMN_STORAGE_KEY);
    }
  }, [selectedChartColumn]);

  const handleSaveColumnsAsDefault = async () => {
    if (!columnsInitialized) {
      alert('❌ Column preferences are still loading.\n\nPlease wait for the page to finish initializing and try again.');
      return;
    }

    if (!visibleColumns || Object.keys(visibleColumns).length === 0) {
      alert('❌ No column configuration found to save.\n\nPlease wait for the page to fully load and try again.');
      return;
    }

    if (!columnOrder || columnOrder.length === 0) {
      alert('❌ No column order found to save.\n\nPlease wait for the page to fully load and try again.');
      return;
    }

    setShowSaveDefaultModal(true);
  };

  const confirmSaveColumnsAsDefault = async () => {
    setShowSaveDefaultModal(false);
    try {
      await saveAsDefaultColumnConfig('all_accounts', visibleColumns, columnOrder);
      const updatedConfig = await getDefaultColumnConfig('all_accounts');
      clearColumnStorage();
      updateColumnPreferencesState(updatedConfig.visibleColumns, updatedConfig.columnOrder);
    } catch (error) {
      console.error('Failed to save column preferences as default for all accounts', error);
      alert('❌ Failed to save column preferences as default.\n\nPlease try again.\n\nError: ' + error.message);
    }
  };
  
  // Save sorting preference when it changes
  useEffect(() => {
    localStorage.setItem('sortConfig-allAccounts', JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Save view mode when it changes
  useEffect(() => {
    localStorage.setItem('accountsViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    let cancelled = false;
    const initializeMarketStatus = async () => {
      try {
        const response = await getLastUpdateTime();
        if (!cancelled && response?.data && typeof response.data.market_closed === 'boolean') {
          updateMarketClosedState(response.data.market_closed);
        }
      } catch (error) {
        // Ignore initializer failures; ticker updates will keep status in sync later
      }
    };
    initializeMarketStatus();
    return () => {
      cancelled = true;
    };
  }, [updateMarketClosedState]);

  const fetchAllAccounts = useCallback(async () => {
    setTableLoading(true);
    setError('');
    
    try {
      const response = await getAllAccounts();
      console.log("All accounts API response:", response.data);
      setAccounts(response.data);
      // Optionally broadcast accounts for other listeners (e.g., nav totals)
      window.dispatchEvent(new CustomEvent('accountsUpdated', { detail: { accounts: response.data } }));
    } catch (err) {
      console.error('Error fetching all accounts:', err);
      setError('Failed to load accounts. Please try again.');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllAccounts();
    
    // Don't clear accounts data from NavigationPanel when leaving this page
    // This ensures consistent percentage calculations across all pages
    return () => {
      // Removed the cleanup event to maintain consistent calculations
    };
  }, [fetchAllAccounts]);

  // Load overall trading statistics (expectancy)
  useEffect(() => {
    const loadStats = async () => {
      try {
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth(), 1);

        const resp = await getTradingStatistics({
          start_date: formatISODate(startDate),
          end_date: formatISODate(now)
        });
        // Use position-weighted expectancy to match Trades page display
        setOverallExpectancy(typeof resp.data?.expectancy_position_weighted === 'number' ? resp.data.expectancy_position_weighted : null);
      } catch (e) {
        setOverallExpectancy(null);
      }
    };
    loadStats();
  }, []);

  // Fetch expectancy per account and cache
  useEffect(() => {
    // Capture the current Set so the cleanup below uses the same instance even though
    // ref.current itself could, in principle, be reassigned before cleanup runs.
    const fetchedEdges = fetchedEdgesRef.current;
    const fetchEdges = async () => {
      try {
        const ids = new Set((accounts || []).map(a => a.account_id));
        if (ids.size === 0) return;

        // Only fetch edges for accounts we haven't fetched yet
        const missing = Array.from(ids).filter(id => !fetchedEdges.has(id));
        if (missing.length === 0) return;

        // Mark these accounts as being fetched to prevent duplicate requests
        missing.forEach(id => fetchedEdges.add(id));

        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        const startISO = formatISODate(startDate);
        const endISO = formatISODate(now);

        const requests = missing.map(id => getTradingStatistics({
          account_id: id,
          start_date: startISO,
          end_date: endISO
        })
          .then(r => {
            const weighted = (typeof r.data?.expectancy_position_weighted === 'number') ? r.data.expectancy_position_weighted : null;
            const raw = (typeof r.data?.expectancy === 'number') ? r.data.expectancy : null;
            return { id, val: (weighted != null ? weighted : raw) };
          })
          .catch(() => ({ id, val: null }))
        );
        const results = await Promise.all(requests);
        const updates = results.reduce((acc, { id, val }) => { acc[id] = val; return acc; }, {});
        setAccountEdgeMap(prev => ({ ...prev, ...updates }));
      } catch {}
    };
    
    // Clear any existing timeout
    if (fetchEdgesTimeoutRef.current) {
      clearTimeout(fetchEdgesTimeoutRef.current);
    }
    
    // Only fetch edges if we have accounts and missing edges, with debouncing
    if (accounts && accounts.length > 0) {
      fetchEdgesTimeoutRef.current = setTimeout(() => {
        fetchEdges();
      }, 500); // 500ms debounce
    }
    
    // Cleanup function to reset the ref when accounts change significantly
    return () => {
      if (fetchEdgesTimeoutRef.current) {
        clearTimeout(fetchEdgesTimeoutRef.current);
      }
      // Only reset if accounts is empty or significantly different
      if (!accounts || accounts.length === 0) {
        fetchedEdges.clear();
      }
    };
  }, [accounts]);

  // Add WebSocket subscription to automatically refresh account data when ticker prices change
  useEffect(() => {
    // console.log('AllAccounts: Setting up ticker update subscription');

    const unsubscribe = subscribeToTickerUpdates({
      autoReactivate: false,
      callback: (data) => {
        const reopened = updateMarketClosedState(data?.market_closed);

        // Skip refresh entirely while market is closed or outside trading hours
        if (marketClosedRef.current || !isMarketOpen()) {
          return;
        }

        const hasTickerUpdates = Boolean(data && Array.isArray(data.updated_tickers) && data.updated_tickers.length > 0);
        if (reopened || hasTickerUpdates) {
          fetchAllAccounts();
          // Refresh intraday gain data when accounts are updated
          fetchTodayGainIntradayHistory();
        }

        if (reopened) {
          fetchStatusTickers();
        }

        if (hasTickerUpdates) {
          updateStatusTickersFromPayload(data.updated_tickers);
        }
      }
    });

    return () => {
      console.log('AllAccounts: Cleaning up ticker update subscription');
      unsubscribe();
    };
  }, [fetchAllAccounts, fetchStatusTickers, fetchTodayGainIntradayHistory, updateMarketClosedState, updateStatusTickersFromPayload]);

  // Fetch portfolios when modal opens
  useEffect(() => {
    if (showCreateModal) {
      const userId = localStorage.getItem('userId');
      if (userId) {
        setPortfolioLoading(true);
        getPortfolios(userId)
          .then(res => {
            setPortfolios(res.data);
            if (res.data.length > 0) {
              setSelectedPortfolioId(res.data[0].portfolio_id);
            }
          })
          .catch(() => setPortfolios([]))
          .finally(() => setPortfolioLoading(false));
      }
    }
  }, [showCreateModal]);

  // Process accounts to fix gain calculation issues with cash accounts
  const processedAccounts = useMemo(() => {
    if (!accounts || accounts.length === 0) return [];
    
    return accounts.map(account => {
      // Clone the account to avoid mutating the original
      const processedAccount = {...account};
      
      // Fix for cash-only accounts with no previous day balance
      if (account.previous_day_balance === 0 && account.balance > 0) {
        const cashPercentage = (account.cash || 0) / account.balance;
        if (cashPercentage > 0.9) { // If account is 90%+ cash
          // For mostly cash accounts with no previous day value, assume 0 gain
          processedAccount.today_gain_amount = 0;
          processedAccount.today_gain_percentage = 0;
        }
      }
      
      return processedAccount;
    });
  }, [accounts]);

  // Listen for portfoliosUpdated to keep totals aligned if another view fetched
  useEffect(() => {
    const handler = (event) => {
      const portfolios = event?.detail?.portfolios;
      if (!portfolios) return;
      // Derive aggregated accounts from portfolios if present (keep simple sync of totals)
      // If the page already fetches accounts separately, we skip heavy merging to avoid mismatches.
      // We can trigger a lightweight refresh here if desired.
      // For now, do nothing to avoid overriding accounts list; future: map portfolios->accounts.
    };
    window.addEventListener('portfoliosUpdated', handler);
    return () => window.removeEventListener('portfoliosUpdated', handler);
  }, []);

  // Helper function to safely calculate values when data is loading
  const safeCalculate = useCallback((calculationFn) => {
    if (loading || processedAccounts.length === 0) {
      return 0;
    }
    return calculationFn();
  }, [loading, processedAccounts]);

  // Calculate total for all accounts
  const calculateTotals = useCallback(() => {
    if (!processedAccounts || processedAccounts.length === 0) return null;
    
    // First, calculate the total balance and previous day balance
    const totals = processedAccounts.reduce((total, account) => {
      return {
        balance: total.balance + account.balance,
        cash: total.cash + (account.cash || 0),
        previous_day_balance: total.previous_day_balance + (account.previous_day_balance || 0),
        total_gain_amount: total.total_gain_amount + (account.total_gain_amount || 0),
        ytd_gain_amount: total.ytd_gain_amount + (account.ytd_gain_amount || 0),
        bottom_line: total.bottom_line + (account.bottom_line || 0),
      };
    }, {
      balance: 0,
      cash: 0,
      previous_day_balance: 0,
      total_gain_amount: 0,
      ytd_gain_amount: 0,
      bottom_line: 0,
    });
    
    // Calculate today's gain using previous day balance
    totals.today_gain_amount = processedAccounts.reduce((sum, account) => sum + account.today_gain_amount, 0);
    
    // No need for additional cash check since each account has already been processed
    
    // Calculate percentages based on totals
    totals.today_gain_percentage = totals.previous_day_balance > 0 
      ? (totals.today_gain_amount / totals.previous_day_balance) * 100 
      : 0;
    
    // Denominator is cost basis (balance minus the gain itself), not raw
    // current balance -- matching the backend's own total_gain_percentage
    // formula (portfolios.py) and the ytd_gain_percentage calc just below,
    // which already does this correctly. Dividing by balance understated
    // gains (and overstated losses): e.g. $100 grown to $150 is a 50% gain
    // on its $100 cost basis, not $50/$150 = 33%.
    const totalCostBasis = totals.balance - totals.total_gain_amount;
    totals.total_gain_percentage = totalCostBasis > 0
      ? (totals.total_gain_amount / totalCostBasis) * 100
      : 0;
    
    // Calculate YTD gain percentage
    // Calculate YTD starting balance by subtracting ytd_gain_amount from current balance
    const ytdStartingBalance = totals.balance - totals.ytd_gain_amount;
    totals.ytd_gain_percentage = ytdStartingBalance > 0
      ? (totals.ytd_gain_amount / ytdStartingBalance) * 100
      : 0;
    
    return {
      name: `Total (${processedAccounts.length})`,
      ...totals,
    };
  }, [processedAccounts]);

  // Use useMemo to calculate account totals only when accounts change
  const accountTotals = useMemo(() => {
    return safeCalculate(calculateTotals);
  }, [safeCalculate, calculateTotals]);

  // Note: Intraday gain snapshots are now recorded by backend scheduled task
  // Frontend only fetches and displays the data

  // Helper for calculating YTD percentage across all accounts
  const calculateYtdPercentage = () => {
    if (!processedAccounts || processedAccounts.length === 0) return 0;
    
    const totalYtdGain = processedAccounts.reduce((sum, account) => sum + (account.ytd_gain_amount || 0), 0);
    const totalYtdStartingBalance = processedAccounts.reduce((sum, account) => {
      const currentBalance = account.balance || 0;
      const ytdGain = account.ytd_gain_amount || 0;
      return sum + (currentBalance - ytdGain);
    }, 0);
    
    return totalYtdStartingBalance > 0 ? (totalYtdGain / totalYtdStartingBalance) * 100 : 0;
  };

  // Memoize filtered accounts to prevent unnecessary re-renders
  const filteredAccounts = useMemo(() => {
    return accounts.filter(account => account.balance > 0);
  }, [accounts]);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!selectedPortfolioId) {
      setCreateError('Please select a portfolio');
      return;
    }
    if (!newAccountName.trim()) {
      setCreateError('Account name is required');
      return;
    }
    if (isNaN(parseFloat(newAccountCash)) || parseFloat(newAccountCash) < 0) {
      setCreateError('Initial cash must be a valid number greater than or equal to 0');
      return;
    }
    setCreateError('');
    setCreateLoading(true);
    try {
      await createAccount({
        portfolio_id: selectedPortfolioId,
        name: newAccountName.trim(),
        description: newAccountDescription.trim(),
        balance: parseFloat(newAccountCash),
        cash: parseFloat(newAccountCash)
      });
      await fetchAllAccounts();
      setNewAccountName('');
      setNewAccountDescription('');
      setNewAccountCash('');
      setSelectedPortfolioId(portfolios[0]?.portfolio_id || '');
      setShowCreateModal(false);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        setCreateError(err.response.data.error);
      } else {
        setCreateError('Failed to create account. Please try again.');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  // Helper function to get calendar month period label
  const getCalendarMonthLabel = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1); // 1st day of current month
    
    const startMonth = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endMonth = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    return `${startMonth} - ${endMonth}`;
  };

  // AccountCard component for cards view
  const AccountCard = ({ account, edgeValue }) => {
    const todayGainAmount = account.today_gain_amount || 0;
    const todayGainPct = account.today_gain_percentage || 0;
    const totalGainAmount = account.total_gain_amount || 0;
    const totalGainPct = account.total_gain_percentage || 0;
    const ytdGainAmount = account.ytd_gain_amount || 0;
    const ytdGainPct = account.ytd_gain_percentage || 0;

    // Determine background color based on today's gain
    const getBackgroundStyle = () => {
      if (todayGainAmount === 0) return {};
      
      const isGain = todayGainAmount > 0;
      return {
        backgroundColor: isGain 
          ? 'rgba(25, 135, 84, 0.05)' // Light green for gains
          : 'rgba(220, 53, 69, 0.05)', // Light red for losses
        borderColor: isGain 
          ? 'rgba(25, 135, 84, 0.2)' 
          : 'rgba(220, 53, 69, 0.2)'
      };
    };
    
    return (
      <div className="col-lg-4 col-md-6 col-sm-12 mb-2">
        <Link 
          to={`/accounts/${account.account_id}`} 
          className="text-decoration-none"
          style={{ color: 'inherit' }}
        >
          <Card 
            className="h-100 accounts-card account-card clickable-card"
            style={getBackgroundStyle()}
          >
            <Card.Body className="p-2">
              <div className="d-flex justify-content-between align-items-start mb-1">
                <div className="flex-grow-1">
                  <Card.Title className="fs-6 mb-0 fw-bold">
                    {account.name || account.account_name}
                  </Card.Title>
                  <Card.Subtitle className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {account.portfolio_name}
                  </Card.Subtitle>
                </div>
                <div className="text-end">
                  <div className={`small fw-bold ${edgeValue >= 0 ? 'text-success' : 'text-danger'}`}>
                    {edgeValue != null ? `${edgeValue.toFixed(1)}%` : '-'} <span className="text-muted" style={{ fontSize: '0.65rem', fontWeight: 'normal' }}>Edge</span>
                  </div>
                </div>
              </div>
              
              <div className="row g-1 mb-1">
                <div className="col-6">
                  <div className="d-flex align-items-center gap-1">
                    <span className="text-muted" style={{ fontSize: '0.7rem' }}>Balance:</span>
                    <span className="fw-bold small">{formatBalanceDisplay(account.balance)}</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center gap-1">
                    <span className="text-muted" style={{ fontSize: '0.7rem' }}>Cash:</span>
                    <span className="small">{formatBalanceDisplay(account.cash || 0)}</span>
                  </div>
                </div>
              </div>
              
              <div className="row g-1 text-center">
                <div className="col-4">
                  <div className={`fw-bold ${todayGainAmount >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.8rem' }}>
                    {formatBalanceDisplay(todayGainAmount)}
                  </div>
                  <div className={`${todayGainPct >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.65rem' }}>
                    {todayGainPct >= 0 ? '+' : ''}{todayGainPct.toFixed(1)}%
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.6rem' }}>Today</div>
                </div>
                <div className="col-4">
                  <div className={`fw-bold ${totalGainAmount >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.8rem' }}>
                    {formatBalanceDisplay(totalGainAmount)}
                  </div>
                  <div className={`${totalGainPct >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.65rem' }}>
                    {totalGainPct >= 0 ? '+' : ''}{totalGainPct.toFixed(1)}%
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.6rem' }}>Total</div>
                </div>
                <div className="col-4">
                  <div className={`fw-bold ${ytdGainAmount >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.8rem' }}>
                    {formatBalanceDisplay(ytdGainAmount)}
                  </div>
                  <div className={`${ytdGainPct >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.65rem' }}>
                    {ytdGainPct >= 0 ? '+' : ''}{ytdGainPct.toFixed(1)}%
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.6rem' }}>YTD</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Link>
      </div>
    );
  };

  if (error) {
    return (
      <Container>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container style={{ marginTop: '-0.5rem' }}>
      <style>{customStyles}</style>

      <Row className="mb-2">
        <Col className="d-flex flex-wrap">
          <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
            <Card className="h-100 accounts-card">
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">Total Balance</Card.Title>
                <div className="text-muted small" style={{height: "18px"}}></div>
                {loading ? (
                  <>
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={6} className="fs-4" />
                    </Placeholder>
                    <div style={{height: "19px"}}></div>
                  </>
                ) : (
                  <>
                    <Card.Text className="fs-4 mb-0">
                      {formatBalanceDisplay(accountTotals?.balance)}
                    </Card.Text>
                    <small className="text-muted d-block">
                      {(() => {
                        const balance = accountTotals?.balance || 0;
                        const cash = accountTotals?.cash || 0;
                        // Exposure = 100% - cash percentage
                        const exposurePct = balance > 0 ? ((balance - cash) / balance) * 100 : 0;
                        return `(${exposurePct.toFixed(1)}%)`;
                      })()}
                    </small>
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
          <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
            <Card className="h-100 accounts-card">
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">
                  Bottom Line
                  <i
                    className="bi bi-info-circle ms-1"
                    style={{ fontSize: '0.8rem', cursor: 'help' }}
                    title="Bottom line risks calculate with current account value. If you see bigger negative risks, consider raising your stop loss to reduce bottom line risks."
                  ></i>
                </Card.Title>
                <div className="text-muted small" style={{height: "18px"}}>If all stop losses hit</div>
                {loading ? (
                  <>
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={8} className="fs-5" />
                    </Placeholder>
                    <div style={{height: "19px"}}></div>
                  </>
                ) : (
                  <>
                    <Card.Text className="fs-5 mb-0 fw-bold">
                      {formatBalanceDisplay(accountTotals?.bottom_line)}
                    </Card.Text>
                    <span className="d-block small text-muted">
                      ({accountTotals?.balance > 0 && accountTotals?.bottom_line !== undefined ? ((accountTotals.bottom_line / accountTotals.balance) * 100).toFixed(2) : '0.00'}%)
                      {accountTotals?.balance > 0 && accountTotals?.bottom_line !== undefined && (
                        <span
                          style={{
                            color: (() => {
                              const pct = ((accountTotals.bottom_line / accountTotals.balance) * 100);
                              const effect = pct - 100;
                              if (effect < -3) return 'red';
                              if (effect > -3) return 'goldenrod';
                              return 'inherit';
                            })(),
                            marginLeft: 6,
                          }}
                        >
                          ({(() => {
                            const pct = ((accountTotals.bottom_line / accountTotals.balance) * 100);
                            const effect = pct - 100;
                            return `${effect >= 0 ? '+' : ''}${effect.toFixed(2)}%`;
                          })()})
                        </span>
                      )}
                    </span>
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
          <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
            <Card className="h-100 accounts-card">
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">Cash Balance</Card.Title>
                <div className="text-muted small" style={{height: "18px"}}></div>
                {loading ? (
                  <Placeholder as={Card.Text} animation="glow" className="mb-0">
                    <Placeholder xs={8} className="fs-5" />
                  </Placeholder>
                ) : (
                  <Card.Text className="fs-5 mb-0 fw-bold">
                    {formatBalanceDisplay(accountTotals?.cash)}
                  </Card.Text>
                )}
                {loading ? (
                  <div style={{height: "19px"}}></div>
                ) : (
                  <span className="d-block small text-muted">
                    ({accountTotals?.balance > 0 ? (accountTotals.cash / accountTotals.balance * 100).toFixed(1) : 0}%)
                  </span>
                )}
              </Card.Body>
            </Card>
          </div>
          <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
            <Card className="h-100 accounts-card" style={{ cursor: 'pointer' }} onClick={() => setShowEdgeHistoryModal(true)}>
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">
                  Trading Edge
                  <i
                    className="bi bi-info-circle ms-1"
                    style={{ fontSize: '0.8rem', cursor: 'help' }}
                    title="Trading edge (expectancy) from CLOSED trades only (1st of current month to today). Shows raw expectancy and a secondary position-weighted expectancy that accounts for position size via standard size multiples."
                  ></i>
                </Card.Title>
                <div className="text-muted small" style={{height: "18px"}}>{getCalendarMonthLabel()}</div>
                <div className="text-muted small" style={{fontSize: '0.7rem', height: "16px"}}>Closed Trades Only</div>
                {loading ? (
                  <>
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={6} className="fs-5" />
                    </Placeholder>
                    <div style={{height: "19px"}}></div>
                  </>
                ) : (
                  <>
                    <Card.Text className={`fs-5 mb-0 fw-bold text-${(overallExpectancy || 0) >= 0 ? 'success' : 'danger'}`}>
                      {overallExpectancy != null ? `${overallExpectancy.toFixed(2)}%` : '-'}
                    </Card.Text>
                    <small className={`d-block small text-${(overallExpectancy || 0) >= 0 ? 'success' : 'danger'}`}>
                      {(overallExpectancy || 0) >= 0 ? 'Profitable Strategy' : 'Losing Strategy'}
                    </small>
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
          <div className="col-xl col-lg-4 col-md-6 col-sm-6 pb-3 px-1">
            <Card
              className="h-100 accounts-card"
            >
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">Today's Gain</Card.Title>
                <small className="text-muted d-block" style={{height: "18px", visibility: loading ? 'hidden' : 'visible'}}>prev bal basis</small>
                {loading ? (
                  <>
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={7} className="fs-5" />
                    </Placeholder>
                    <div style={{height: "19px"}}></div>
                  </>
                ) : (
                  <>
                    {(() => {
                      const amount = normalizeNearZero(accountTotals?.today_gain_amount || 0);
                      return (
                        <Card.Text className={`fs-5 mb-0 fw-bold text-${amount >= 0 ? 'success' : 'danger'}`}>
                          {formatBalanceDisplay(amount)}
                        </Card.Text>
                      );
                    })()}
                    {(() => {
                      const pct = normalizeNearZero(accountTotals?.today_gain_percentage || 0);
                      return (
                        <span className={`d-block small text-${pct >= 0 ? 'success' : 'danger'}`}>
                          ({pct < 0 ? '-' : ''}{Math.abs(pct).toFixed(2)}%)
                        </span>
                      );
                    })()}
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
          <div className="col-xl col-lg-6 col-md-6 col-sm-6 pb-3 px-1">
            <Card
              className="h-100 accounts-card"
            >
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">Total Gain</Card.Title>
                <div className="text-muted small" style={{height: "18px"}}></div>
                {loading ? (
                  <Placeholder as={Card.Text} animation="glow" className="mb-0">
                    <Placeholder xs={7} className="fs-5" />
                  </Placeholder>
                ) : (
                  <Card.Text className={`fs-5 mb-0 fw-bold text-${accountTotals?.total_gain_amount >= 0 ? 'success' : 'danger'}`}>
                    {formatBalanceDisplay(accountTotals?.total_gain_amount)}
                  </Card.Text>
                )}
                {loading ? (
                  <div style={{height: "19px"}}></div>
                ) : (
                  <span className={`d-block small text-${accountTotals?.total_gain_amount >= 0 ? 'success' : 'danger'}`}>
                    ({accountTotals?.total_gain_percentage.toFixed(2) || "0.00"}%)
                  </span>
                )}
              </Card.Body>
            </Card>
          </div>
          <div className="col-xl col-lg-6 col-md-6 col-sm-6 pb-3 px-1">
            <Card
              className="h-100 accounts-card"
            >
              <Card.Body className="p-2 text-center">
                <Card.Title className="fs-6 mb-1">Year-to-Date</Card.Title>
                <div className="text-muted small" style={{height: "18px"}}>
                  {!loading && accounts.some(a => a.ytd_start_date) && (
                    <small className="text-muted d-block">since {new Date(accounts.filter(a => a.ytd_start_date)[0].ytd_start_date).toLocaleDateString()}</small>
                  )}
                </div>
                {loading ? (
                  <>
                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                      <Placeholder xs={7} className="fs-5" />
                    </Placeholder>
                    <div style={{height: "19px"}}></div>
                  </>
                ) : (
                  <>
                    <Card.Text className={`fs-5 mb-0 fw-bold text-${accounts.reduce((sum, account) => sum + (account.ytd_gain_amount || 0), 0) >= 0 ? 'success' : 'danger'}`}>
                      {formatBalanceDisplay(accounts.reduce((sum, account) => sum + (account.ytd_gain_amount || 0), 0))}
                    </Card.Text>
                    <span className={`d-block small text-${accounts.reduce((sum, account) => sum + (account.ytd_gain_amount || 0), 0) >= 0 ? 'success' : 'danger'}`}>
                      ({calculateYtdPercentage().toFixed(2)}%)
                    </span>
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex flex-wrap align-items-center">
              <Button
                variant="outline-primary"
                onClick={() => setShowBalanceHistoryModal(true)}
                size="sm"
                className="me-2 mb-2"
              >
                <i className="bi bi-graph-up"></i> Balance
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => setShowPerformanceModal(true)}
                size="sm"
                className="me-2 mb-2"
              >
                <i className="bi bi-speedometer2"></i> Performance
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => setShowPieChartModal(true)}
                size="sm"
                className="me-2 mb-2"
              >
                <i className="bi bi-pie-chart"></i> Pie Chart
              </Button>
              <Button 
                variant="outline-primary"
                onClick={() => setShowCreateModal(true)}
                size="sm"
                className="mb-2"
              >
                <i className="bi bi-plus"></i> Account
              </Button>
            </div>
            
            {/* View toggle and Column configuration dropdown */}
            <div className="d-flex gap-2">
              {/* View mode toggle */}
              <div className="btn-group" role="group">
                <Button
                  variant={viewMode === 'charts' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setViewMode('charts')}
                  disabled={availableChartColumns.length === 0}
                >
                  <i className="bi bi-bar-chart-line"></i> Charts
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                >
                  <i className="bi bi-table"></i> Table
                </Button>
                <Button
                  variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                >
                  <i className="bi bi-grid-3x3-gap"></i> Cards
                </Button>
              </div>

              {/* Chart column selector dropdown - only for charts view */}
              {viewMode === 'charts' && availableChartColumns.length > 0 && (
                <Dropdown className="d-inline-block" align="end">
                  <Dropdown.Toggle
                    variant="outline-primary"
                    size="sm"
                    id="chart-column-dropdown"
                    disabled={loading}
                  >
                    {selectedChartColumn
                      ? accountColumnDefinitions[selectedChartColumn]?.chartLabel || accountColumnDefinitions[selectedChartColumn]?.label || selectedChartColumn
                      : 'Select Column'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {chartColumnOrder.map(columnKey => {
                      const column = accountColumnDefinitions[columnKey];
                      if (!column || !isAccountNumericColumn(columnKey)) {
                        return null;
                      }

                      const isActive = selectedChartColumn === columnKey;
                      const label = column.chartLabel || column.label;

                      return (
                        <Dropdown.Item
                          key={columnKey}
                          active={isActive}
                          onClick={() => handleChartHeaderClick(columnKey)}
                        >
                          {label}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              )}

              {/* Column configuration dropdown - table view only */}
              {viewMode === 'table' && visibleColumns && (
                <ColumnVisibilityDropdown
                  visibleColumns={visibleColumns}
                  columns={Object.keys(visibleColumns)}
                  onToggleColumn={(column, newValue) => {
                    setVisibleColumns(prev => ({
                      ...prev,
                      [column]: newValue
                    }));
                  }}
                  idPrefix="header-column-toggle"
                  switchesDisabled={!columnsInitialized}
                  disabled={loading || !columnsInitialized}
                >
                  <Dropdown.Divider />
                  <div className="text-muted small">
                    <div>• Click column headers to sort</div>
                  </div>
                  <Dropdown.Divider />
                  <div className="d-grid gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="w-100"
                      disabled={!columnsInitialized}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveColumnsAsDefault();
                      }}
                    >
                      Set as Default
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="w-100"
                      disabled={!columnsInitialized}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResetColumnsToUserDefaults();
                      }}
                    >
                      Reset Columns
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="w-100"
                      disabled={!columnsInitialized}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResetColumnsToSystemDefaults();
                      }}
                    >
                      Reset to System Default
                    </Button>
                  </div>
                </ColumnVisibilityDropdown>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Table view */}
      {viewMode === 'table' && (
        <div className="accounts-table-container">
          <div className="table-responsive">
            <AccountPortfolioTable
              items={processedAccounts.map(account => ({
                ...account,
                portfolioInfo: account.portfolio_name
              }))}
              type="account"
              showTotal={processedAccounts.length > 0}
              totalData={accountTotals}
              showPortfolioColumn={true}
              sortConfig={sortConfig}
              onSortChange={(key, direction) => setSortConfig({ key, direction })}
              tableLoading={tableLoading}
              visibleColumns={visibleColumns}
              columnOrder={columnOrder}
              accountEdgeMap={accountEdgeMap}
              className="accounts-table"
            />
          </div>
        </div>
      )}

      {/* Cards view */}
      {viewMode === 'cards' && (
        <div className="accounts-cards-container">
          {tableLoading ? (
            <Row>
              {[1, 2, 3, 4, 5, 6].map(index => (
                <div key={index} className="col-lg-4 col-md-6 col-sm-12 mb-2">
                  <Card className="h-100 accounts-card account-card">
                    <Card.Body className="p-2">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div className="flex-grow-1">
                          <Placeholder as={Card.Title} animation="glow" className="mb-0">
                            <Placeholder xs={7} />
                          </Placeholder>
                          <Placeholder as={Card.Subtitle} animation="glow">
                            <Placeholder xs={5} />
                          </Placeholder>
                        </div>
                        <Placeholder animation="glow">
                          <Placeholder xs={3} />
                        </Placeholder>
                      </div>
                      <div className="row g-1 mb-1">
                        <div className="col-6">
                          <Placeholder animation="glow">
                            <Placeholder xs={12} />
                          </Placeholder>
                        </div>
                        <div className="col-6">
                          <Placeholder animation="glow">
                            <Placeholder xs={12} />
                          </Placeholder>
                        </div>
                      </div>
                      <div className="row g-1">
                        <div className="col-4">
                          <Placeholder animation="glow" className="text-center">
                            <Placeholder xs={10} />
                          </Placeholder>
                        </div>
                        <div className="col-4">
                          <Placeholder animation="glow" className="text-center">
                            <Placeholder xs={10} />
                          </Placeholder>
                        </div>
                        <div className="col-4">
                          <Placeholder animation="glow" className="text-center">
                            <Placeholder xs={10} />
                          </Placeholder>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </Row>
          ) : (
            <Row>
              {processedAccounts.map(account => {
                const edgeValue = accountEdgeMap && account.account_id && typeof accountEdgeMap[account.account_id] === 'number'
                  ? accountEdgeMap[account.account_id]
                  : (typeof account.expectancy === 'number' ? account.expectancy : 0);

                return (
                  <AccountCard
                    key={account.account_id}
                    account={account}
                    edgeValue={edgeValue}
                  />
                );
              })}
            </Row>
          )}
        </div>
      )}

      {/* Charts view */}
      {viewMode === 'charts' && (
        availableChartColumns.length === 0 ? (
          <Alert variant="info">
            No numeric columns are visible. Enable at least one numeric column to view charts.
          </Alert>
        ) : (
          <>
            {selectedChartColumn ? (
              <AccountsChartView
                accounts={processedAccounts}
                selectedColumn={selectedChartColumn}
                accountEdgeMap={accountEdgeMap}
                isLoading={tableLoading}
                todayGainIntradaySeries={todayGainIntradaySeries}
                statusTickers={statusTickers}
                intradayPeriod={intradayPeriod}
                onIntradayPeriodChange={setIntradayPeriod}
                onTickerSelectionChange={fetchStatusTickers}
              />
            ) : (
              <Alert variant="info">
                Select a numeric column to display charts.
              </Alert>
            )}
          </>
        )
      )}
      

      
      {!tableLoading && accounts.length === 0 && (
        <div className="text-center my-5">
          <p className="mb-4">You don't have any accounts yet.</p>
          <Button 
            variant="primary" 
            as={Link} 
            to="/dashboard"
            size="sm"
          >
            Create Your First Account
          </Button>
        </div>
      )}

      {/* Pie Chart Modal */}
      <PieChartModal
        show={showPieChartModal}
        onHide={() => setShowPieChartModal(false)}
        portfolios={filteredAccounts}
        cash={0}
        title="All Accounts Distribution"
      />
      <BalanceHistoryModal
        show={showBalanceHistoryModal}
        onHide={() => setShowBalanceHistoryModal(false)}
        isAggregated={true}
        portfolioName={"All Accounts"}
        currentBalance={calculateTotals()?.balance || null}
        isBalanceLoading={tableLoading}
      />
      <PerformanceChartModal
        show={showPerformanceModal}
        onHide={() => setShowPerformanceModal(false)}
        isAggregated={true}
        title="All Accounts Performance"
        currentBalance={calculateTotals()?.balance || null}
        isBalanceLoading={tableLoading}
      />
      {/* Edge History Modal */}
      <EdgeHistoryModal
        show={showEdgeHistoryModal}
        onHide={() => setShowEdgeHistoryModal(false)}
        accountId={edgeHistoryAccountId}
        accountName={undefined}
        timePeriod={edgeHistoryTimePeriod}
        onTimePeriodChange={setEdgeHistoryTimePeriod}
        accounts={(accounts || []).map(a => ({ account_id: a.account_id, name: a.name || a.account_name }))}
        onAccountChange={(newAccountId) => setEdgeHistoryAccountId(newAccountId)}
      />
      {/* Save as Default Confirmation Modal */}
      <Modal show={showSaveDefaultModal} onHide={() => setShowSaveDefaultModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Save Column Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Save your current column configuration as your personal default?</p>
          <p className="mb-2">This will apply to the All Accounts table and includes:</p>
          <ul className="mb-0">
            <li>{Object.values(visibleColumns).filter(Boolean).length} visible columns</li>
            <li>Current column order</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveDefaultModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmSaveColumnsAsDefault}>
            Save as Default
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Reset to System Defaults Confirmation Modal */}
      <Modal show={showResetSystemDefaultModal} onHide={() => setShowResetSystemDefaultModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Column Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Reset column configuration to the system defaults?</p>
          <p className="mb-0">This will clear your personal default for all accounts.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetSystemDefaultModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmResetColumnsToSystemDefaults}>
            Reset to System Default
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createError && (
            <Alert variant="danger">{createError}</Alert>
          )}
          <Form onSubmit={handleCreateAccount}>
            <Form.Group className="mb-3" controlId="portfolioSelect">
              <Form.Label>Portfolio</Form.Label>
              <Form.Select
                value={selectedPortfolioId}
                onChange={e => setSelectedPortfolioId(e.target.value)}
                required
                disabled={portfolioLoading}
              >
                {portfolioLoading && <option>Loading...</option>}
                {!portfolioLoading && portfolios.length === 0 && <option value="">No portfolios found</option>}
                {!portfolioLoading && portfolios.map(p => (
                  <option key={p.portfolio_id} value={p.portfolio_id}>{p.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="accountName">
              <Form.Label>Account Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter account name"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="accountDescription">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter account description"
                value={newAccountDescription}
                onChange={(e) => setNewAccountDescription(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="accountCash">
              <Form.Label>Initial Total Balance (cash + stocks)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                placeholder="Enter initial total balance"
                value={newAccountCash}
                onChange={(e) => setNewAccountCash(e.target.value)}
                required
              />
            </Form.Group>
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit"
                disabled={createLoading}
              >
                {createLoading ? 'Creating...' : 'Create Account'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default AllAccounts; 
